import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const getDashboardPath = (type) => {
  if (type === 'admin') return '/admin';
  if (type === 'partner') return '/partner';
  return '/staff';
};

const Navbar = (props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  });
  const navigate = useNavigate();

  // Listen for login/logout changes (storage and custom event)
  useEffect(() => {
    const updateUser = () => {
      try {
        setUser(JSON.parse(localStorage.getItem('user')));
      } catch {
        setUser(null);
      }
    };
    window.addEventListener('storage', updateUser);
    window.addEventListener('user-login', updateUser);
    return () => {
      window.removeEventListener('storage', updateUser);
      window.removeEventListener('user-login', updateUser);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  return (
    <nav className="fixed inset-x-0 top-0 z-50 w-full border-b border-yellow-100 bg-white/95 shadow-md backdrop-blur font-official">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="text-2xl font-bold text-black">Dani Job Club</Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex space-x-4 items-center">
            <Link to="/" className="text-secondary hover:text-primary px-3 py-2 rounded-md text-md font-medium">Home</Link>
            <Link to="/services" className="text-secondary hover:text-primary px-3 py-2 rounded-md text-md font-medium">Services</Link>
            <Link to="/jobs" className="text-yellow-400 hover:text-black px-3 py-2 rounded-md text-md font-bold">Jobs</Link>
            <Link to="/partnership" className="text-secondary hover:text-primary px-3 py-2 rounded-md text-md font-medium">Partnership</Link>
            <Link to="/internship" className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md text-md font-bold">Internship</Link>
            <Link to="/contact" className="text-secondary hover:text-primary px-3 py-2 rounded-md text-md font-medium">Contact</Link>
            {!user ? (
              <>
                <Link to="/login" className="text-secondary hover:text-primary px-3 py-2 rounded-md text-md font-medium">Log in</Link>
                <button
                  type="button"
                  onClick={props.openRegisterModal}
                  className="ml-4 bg-black text-yellow-400 px-5 py-2 rounded-full font-bold text-md shadow-lg animate-pulse border-2 border-black blink-btn"
                >
                  Register now!
                </button>
              </>
            ) : (
              <>
                <span className="px-3 py-2 text-md font-bold text-black">{user.name || user.email}</span>
                <Link to={getDashboardPath(user.type)} className="bg-black text-yellow-400 px-4 py-2 rounded font-bold hover:bg-gray-800 transition">My Dashboard</Link>
                <button onClick={handleLogout} className="bg-yellow-400 text-black px-4 py-2 rounded font-bold hover:bg-yellow-300">Logout</button>
              </>
            )}
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 rounded focus:outline-none"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-black my-1 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-yellow-100 shadow-lg px-4 pb-4">
          <div className="flex flex-col space-y-1 pt-2">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-secondary hover:text-primary px-3 py-2 rounded-md text-base font-medium">Home</Link>
            <Link to="/services" onClick={() => setMenuOpen(false)} className="text-secondary hover:text-primary px-3 py-2 rounded-md text-base font-medium">Services</Link>
            <Link to="/jobs" onClick={() => setMenuOpen(false)} className="text-yellow-400 hover:text-black px-3 py-2 rounded-md text-base font-bold">Jobs</Link>
            <Link to="/partnership" onClick={() => setMenuOpen(false)} className="text-secondary hover:text-primary px-3 py-2 rounded-md text-base font-medium">Partnership</Link>
            <Link to="/internship" onClick={() => setMenuOpen(false)} className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md text-base font-bold">Internship</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="text-secondary hover:text-primary px-3 py-2 rounded-md text-base font-medium">Contact</Link>
            {!user ? (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="text-secondary hover:text-primary px-3 py-2 rounded-md text-base font-medium">Log in</Link>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); props.openRegisterModal(); }}
                  className="mt-2 bg-black text-yellow-400 px-5 py-2 rounded-full font-bold text-base shadow-lg text-center animate-pulse border-2 border-black blink-btn"
                >
                  Register now!
                </button>
              </>
            ) : (
              <>
                <span className="px-3 py-2 text-base font-bold text-black">{user.name || user.email}</span>
                <Link to={getDashboardPath(user.type)} onClick={() => setMenuOpen(false)} className="bg-black text-yellow-400 px-4 py-2 rounded font-bold hover:bg-gray-800 transition mt-2 text-center">My Dashboard</Link>
                <button onClick={() => { setMenuOpen(false); handleLogout(); }} className="bg-yellow-400 text-black px-4 py-2 rounded font-bold hover:bg-yellow-300 mt-2">Logout</button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;